class QuizSlider extends HTMLElement {
	connectedCallback() {
		this.section = this.closest('[data-quiz-section]');
		this.headingContainer = this.section?.querySelector(
			'.quiz-heading__container'
		);
		this.viewport = this.querySelector('[data-quiz-viewport]');
		this.resultSlide = this.querySelector('[data-quiz-result-slide]');
		this.track = this.querySelector('[data-quiz-track]');
		this.progress = this.querySelector('[data-quiz-progress]');
		this.counter = this.querySelector('[data-quiz-counter]');
		this.navigation = this.querySelector('[data-quiz-navigation]');
		this.previousButton = this.querySelector('[data-quiz-previous]');
		this.nextButton = this.querySelector('[data-quiz-next]');
		this.form = this.querySelector('[data-quiz-form]');
		this.submitButton = this.form?.querySelector('[type="submit"]');
		this.resultMessage = this.querySelector(
			'[data-quiz-result-message]'
		);
		this.allSlides = [
			...this.querySelectorAll('[data-quiz-slide]'),
		];
		this.skipButton = this.querySelector('[data-quiz-skip]');
		this.quizDescription = this.querySelector(
			'[data-quiz-description]'
		);
		this.pageDescription = this.section?.querySelector(
			'[data-quiz-page-description]'
		);
		this.defaultPageDescription = this.pageDescription?.innerHTML || '';
		this.pageHeading = this.section?.querySelector('[data-quiz-page-heading]');

		if (!this.track || !this.allSlides.length) {
			return;
		}

		this.state = {
			path: null,
			answers: {},
		};
		this.activeIndex = 0;

		this.bindEvents();
		this.updateActiveSlides();

		this.handleResize = () => {
			window.clearTimeout(
				this.resizeTimeout
			);

			this.resizeTimeout = window.setTimeout(
				() => {
					const activeSlide =
						this.slides?.[this.activeIndex];

					if (!activeSlide) return;

					this.updateTrackHeight(
						activeSlide
					);
				},
				150
			);
		};

		window.addEventListener('resize', this.handleResize);
	}


	bindEvents() {
		this.addEventListener('change', (event) => {
			const option = event.target.closest(
				'[data-quiz-option]'
			);

			if (!option) return;

			this.saveQuestionAnswers(option);
			this.scrollToNavigation(option);
		});

		this.previousButton?.addEventListener(
			'click',
			() => {
				this.goTo(this.activeIndex - 1);
			}
		);

		this.nextButton?.addEventListener(
			'click',
			() => {
				if (!this.validateCurrentQuestion()) return;

				this.goTo(this.activeIndex + 1);
			}
		);

		this.form?.addEventListener('submit', (event) => {
			this.handleSubmit(event);
		});

		this.skipButton?.addEventListener('click', () => {
			this.handleSkip();
		});
	}

	updateQuizDescription() {
		if (!this.quizDescription) return;

		const descriptions = {
			city: Theme.translations.city_form_result_body,
			well: Theme.translations.well_form_results_body,
		};

		this.quizDescription.innerHTML =
			descriptions[this.state.path] || '';
	}

	saveQuestionAnswers(option) {
		const question = option.closest('[data-quiz-question]');
		const questionId = question?.dataset.questionId;

		if (!question || !questionId) return;

		const selectedOptions = [
			...question.querySelectorAll(
				'[data-quiz-option]:checked'
			),
		];

		this.state.answers[questionId] =
			selectedOptions.map((selectedOption) =>
				this.createAnswerData(
					selectedOption,
					question
				)
			);

		const unsureMessage = question.querySelector(
			'[data-quiz-unsure-message]'
		);

		const isUnsureSelected =
			selectedOptions.some((selectedOption) =>
				selectedOption.hasAttribute(
					'data-quiz-unsure'
				)
			);

		unsureMessage?.toggleAttribute(
			'hidden',
			!isUnsureSelected
		);

		const routeOption =
			selectedOptions.find(
				(selectedOption) =>
					selectedOption.dataset.quizRoute
			);

		if (!routeOption) return;

		const previousPath = this.state.path;

		this.state.path =
			routeOption.dataset.quizRoute;
		this.updateQuizDescription();

		if (previousPath === this.state.path) {
			return;
		}

		this.clearInactiveAnswers();
		this.updateActiveSlides();
	}

	createAnswerData(option, question) {
		const score = option.dataset.score;

		return {
			label: option.value,

			score:
				score === undefined || score === ''
					? null
					: Number(score),

			sentence:
				question.dataset
					.includePersonalization === 'true'
					? option.dataset.personalization || ''
					: '',

			questionIndex:
				this.allSlides.indexOf(question),

			optionIndex:
				Number(option.dataset.optionIndex) || 0,
		};
	}

	clearInactiveAnswers() {
		this.allSlides
			.filter((slide) => {
				const path = slide.dataset.quizPath;

				return (
					slide.hasAttribute(
						'data-quiz-question'
					) &&
					path !== 'common' &&
					path !== this.state.path
				);
			})
			.forEach((slide) => {
				const questionId =
					slide.dataset.questionId;

				slide
					.querySelectorAll(
						'[data-quiz-option]'
					)
					.forEach((option) => {
						option.checked = false;
					});

				delete this.state.answers[questionId];
			});
	}

	updateActiveSlides() {
		this.slides = this.allSlides.filter(
			(slide) => {
				if (
					!slide.hasAttribute(
						'data-quiz-question'
					)
				) {
					return true;
				}

				const path =
					slide.dataset.quizPath;

				return (
					path === 'common' ||
					path === this.state.path
				);
			}
		);

		this.allSlides.forEach((slide) => {
			slide.hidden =
				!this.slides.includes(slide);
		});

		this.questions = this.slides.filter(
			(slide) =>
				slide.hasAttribute(
					'data-quiz-question'
				)
		);

		this.activeIndex = Math.min(
			this.activeIndex,
			this.slides.length - 1
		);

		this.renderProgress();
		this.update();
	}

	validateCurrentQuestion() {
		const slide =
			this.slides[this.activeIndex];

		if (!slide?.hasAttribute('data-quiz-question')) {
			return true;
		}

		const selected = slide.querySelector(
			'[data-quiz-option]:checked'
		);

		if (selected) {
			this.setError('');

			return true;
		}

		this.setError('Please select an answer before continuing.');

		slide
			.querySelector('[data-quiz-option]')
			?.focus();

		return false;
	}

	goTo(index) {
		if (index < 0 || index >= this.slides.length) return;

		this.activeIndex = index;
		this.setError('');
		this.update();

		if (window.matchMedia('(max-width: 989px)').matches) {
			this.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
	}

	update() {
		const activeSlide =
			this.slides[this.activeIndex];

		if (!activeSlide) return;

		const isFirst =
			this.activeIndex === 0;

		const isForm = activeSlide.hasAttribute(
			'data-quiz-form-slide'
		);

		this.updatePageDescription(
			activeSlide,
			isForm
		);

		this.updateSlidesAccessibility(
			activeSlide
		);

		this.updateProgress(isForm);

		this.updateCounter(
			activeSlide,
			isForm
		);

		if (this.previousButton) {
			this.previousButton.hidden =
				isFirst;
		}

		if (this.nextButton) {
			this.nextButton.hidden =
				isForm;
		}

		this.updateTrackHeight(activeSlide);

		this.track.style.transform =
			`translateX(-${this.activeIndex * 100}%)`;
	}

	updateTrackHeight(activeSlide) {
		activeSlide.style.height = 'auto';

		this.track.style.height =
			`${activeSlide.scrollHeight}px`;
	}

	updateSlidesAccessibility(activeSlide) {
		this.allSlides.forEach((slide) => {
			slide.setAttribute(
				'aria-hidden',
				String(slide !== activeSlide)
			);
		});
	}

	renderProgress() {
		if (!this.progress) return;

		const totalQuestions = this.getTotalQuestions();

		this.progress.style.setProperty('--quiz-slides', totalQuestions);
		this.progress.innerHTML = Array.from(
			{ length: totalQuestions },
			() => {
				return (
					'<span class="quiz-cards__progress-item"></span>'
				);
			}
		).join('');
	}

	updateProgress(isComplete) {
		const activeSlide = this.slides[this.activeIndex];
		const questionIndex = this.questions.indexOf(activeSlide);
		const completed = isComplete
			? this.questions.length
			: questionIndex + 1;

		this.progress?.querySelectorAll('.quiz-cards__progress-item')
			.forEach((item, index) => {
				item.classList.toggle(
					'is-active',
					index < completed
				);
			});
	}

	updateCounter(activeSlide, isForm) {
		if (!this.counter) return;

		if (isForm) {
			this.counter.textContent = '';
			return;
		}

		const questionIndex = this.questions.indexOf(activeSlide);
		const totalQuestions = this.getTotalQuestions();
		this.counter.textContent = `Question ${questionIndex + 1} of ${totalQuestions}`;
	}

	getTotalQuestions() {
		if (this.state.path) return this.questions.length;

		const commonQuestions =
			this.allSlides.filter((slide) => {
				return (
					slide.hasAttribute(
						'data-quiz-question'
					) &&
					slide.dataset.quizPath ===
					'common'
				);
			});

		const pathCounts =
			this.allSlides.reduce(
				(counts, slide) => {
					if (
						!slide.hasAttribute(
							'data-quiz-question'
						)
					) {
						return counts;
					}

					const path =
						slide.dataset.quizPath;

					if (
						!path ||
						path === 'common'
					) {
						return counts;
					}

					counts[path] =
						(counts[path] || 0) + 1;

					return counts;
				},
				{}
			);

		const longestPath = Math.max(
			0,
			...Object.values(pathCounts)
		);

		return (
			commonQuestions.length +
			longestPath
		);
	}

	getScoredAnswers() {
		return Object.values(this.state.answers)
			.flat()
			.filter((answer) =>
				Number.isFinite(answer.score)
			);
	}

	getAverageScore() {
		const answers = this.getScoredAnswers();

		if (!answers.length) return 0;

		const total = answers.reduce(
			(sum, answer) =>
				sum + answer.score,
			0
		);

		return total / answers.length;
	}

	getRecommendationLevel(average) {
		if (average < 1.5) return 'core';

		if (average < 2.25) {
			return 'complete';
		}

		return 'comprehensive';
	}

	getPersonalizationText() {
		return Object.values(this.state.answers)
			.flat()
			.sort((first, second) => {
				return (
					first.questionIndex -
					second.questionIndex ||
					first.optionIndex -
					second.optionIndex
				);
			})
			.map((answer) => answer.sentence)
			.filter(Boolean)
			.join(' ');
	}

	renderResult() {
		const average = this.getAverageScore();
		const level = this.getRecommendationLevel(average);
		const resultKey = `${this.state.path}-${level}`;
		const template = this.querySelector(
			`[data-quiz-result-template="${resultKey}"]`
		);
		const recommendation = this.querySelector('[data-quiz-result-recommendation]');
		const benefits = this.querySelector('[data-quiz-result-benefits]');
		const kit = this.querySelector('[data-quiz-result-kit]');

		if (
			!template ||
			!recommendation ||
			!benefits ||
			!kit
		) {
			console.warn(
				'Unable to render quiz result:',
				{
					path: this.state.path,
					average,
					level,
					resultKey,
					template,
					recommendation,
					benefits,
					kit,
				}
			);

			return false;
		}

		const fallbackTitles = {
			'city-core': Theme.translations.city_core,
			'city-complete': Theme.translations.city_complete,
			'city-comprehensive': Theme.translations.city_comprehensive,
			'well-core': Theme.translations.well_core,
			'well-complete': Theme.translations.well_complete,
			'well-comprehensive': Theme.translations.well_comprehensive,
		};

		const resultTitle =
			template.dataset.resultTitle?.trim() ||
			fallbackTitles[resultKey];

		if (!resultTitle) {
			console.warn(
				`Unable to find a title for quiz result: ${resultKey}`
			);

			return false;
		}

		const fragment = template.content.cloneNode(true);
		const recommendationContent =
			fragment.querySelector(
				'[data-template-recommendation]'
			);
		const benefitsContent =
			fragment.querySelector(
				'[data-template-benefits]'
			);

		if (recommendationContent) {
			recommendation.replaceChildren(
				...recommendationContent.childNodes
			);

			recommendationContent.remove();
		} else {
			recommendation.replaceChildren();
		}

		const personalizationText = this.getPersonalizationText();

		const fixedRecommendation = [
			...recommendation.querySelectorAll(
				'p'
			),
		]
			.map((paragraph) =>
				paragraph.textContent.trim()
			)
			.filter(Boolean)
			.join(' ');

		if (personalizationText) {
			const personalizationParagraph = document.createElement('p');
			personalizationParagraph.textContent = personalizationText;

			recommendation.append(
				personalizationParagraph
			);
		}

		if (benefitsContent) {
			benefits.replaceChildren(
				benefitsContent
			);
		} else {
			benefits.replaceChildren();
		}

		kit.replaceChildren(fragment);

		this.resultData = {
			key: resultKey,
			title: resultTitle,
			averageScore: Number(
				average.toFixed(2)
			),

			fixedRecommendation,
			personalization:
			personalizationText,
		};

		return true;
	}

	async handleSubmit(event) {
		event.preventDefault();

		if (!this.form.reportValidity()) {
			return;
		}

		this.setError('');
		this.setResultMessage('');

		this.submitButton?.setAttribute(
			'aria-busy',
			'true'
		);

		this.submitButton?.setAttribute(
			'disabled',
			''
		);

		try {
			if (!this.renderResult()) {
				throw new Error(
					'Unable to render quiz result'
				);
			}

			const formData =
				new FormData(this.form);

			const payload = {
				firstName: String(
					formData.get('first_name') || ''
				).trim(),
				email: String(
					formData.get('email') || ''
				)
					.trim()
					.toLowerCase(),

				properties:
					this.getQuizProperties(),
			};

			this.showResult();

			await this.submitToKlaviyo(payload);
		} catch (error) {
			console.error('Unable to submit quiz:', error);

			if (this.resultSlide?.hidden) {
				this.setError('Something went wrong. Please try again.');

				return;
			}

			this.setResultMessage(
				'Your results are ready, but we could not save them. Please try again.'
			);
		} finally {
			this.submitButton?.removeAttribute('aria-busy');
			this.submitButton?.removeAttribute('disabled');
		}
	}

	showResult() {
		if (!this.viewport || !this.resultSlide) return;

		this.viewport.hidden = true;
		this.resultSlide.hidden = false;
		this.pageHeading?.classList.add('hidden');
		this.pageDescription?.classList.add('hidden');
		this.section?.classList.add('quiz--result');
		this.headingContainer?.classList.add(
			'quiz-heading__container--result'
		);

		requestAnimationFrame(() => {
			this.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		});
	}

	getQuizProperties() {
		if (!this.resultData) {
			throw new Error('Quiz result data is missing'
			);
		}

		return {
			quiz_type:
			this.state.path,

			quiz_average_score:
			this.resultData.averageScore,

			quiz_recommendation_key:
			this.resultData.key,

			quiz_recommended_kit:
			this.resultData.title,

			quiz_fixed_recommendation:
			this.resultData
				.fixedRecommendation,

			quiz_personalization:
			this.resultData
				.personalization,
		};
	}

	async requestKlaviyo(endpoint, body) {
		const publicApiKey =
			Theme?.klaviyo?.publicKey;

		if (!publicApiKey) {
			throw new Error(
				'Klaviyo public API key is missing'
			);
		}

		const url = new URL(
			`https://a.klaviyo.com/client/${endpoint}`
		);

		url.searchParams.set(
			'company_id',
			publicApiKey
		);

		const response = await fetch(url, {
			method: 'POST',

			headers: {
				accept:
					'application/vnd.api+json',

				'content-type':
					'application/vnd.api+json',

				revision: '2026-07-15',
			},

			body: JSON.stringify(body),
		});

		if (!response.ok) {
			const responseText =
				await response.text();

			throw new Error(
				`Klaviyo request failed: ${response.status} ${responseText}`
			);
		}
	}

	updateKlaviyoProfile({
		email,
	 	firstName,
	 	properties,
 	}) {
		return this.requestKlaviyo(
			'profiles',
			{
				data: {
					type: 'profile',

					attributes: {
						email,
						...(firstName && {
							first_name: firstName,
						}),
						properties,
					},
				},
			}
		);
	}

	createKlaviyoQuizEvent({
 		email,
	 	firstName,
		properties,
 	}) {
		return this.requestKlaviyo(
			'events',
			{
				data: {
					type: 'event',

					attributes: {
						properties,

						metric: {
							data: {
								type: 'metric',

								attributes: {
									name:
										'Quiz Completed',
								},
							},
						},

						profile: {
							data: {
								type: 'profile',

								attributes: {
									email,
									...(firstName && {
										first_name: firstName,
									}),
								},
							},
						},
					},
				},
			}
		);
	}

	submitToKlaviyo({
		email,
		firstName,
		properties,
	}) {
		return Promise.all([
			this.updateKlaviyoProfile({
				email,
				firstName,
				properties,
			}),

			this.createKlaviyoQuizEvent({
				email,
				firstName,
				properties,
			}),
		]);
	}

	setError(message) {
		this.allSlides.forEach((slide) => {
			const error = slide.querySelector(
				'[data-quiz-error]'
			);

			if (error) {
				error.textContent = '';
			}
		});

		if (!message) return;

		const activeSlide =
			this.slides?.[this.activeIndex];

		const activeError =
			activeSlide?.querySelector(
				'[data-quiz-error]'
			);

		if (!activeError) return;

		activeError.textContent = message;
	}

	setResultMessage(message) {
		if (!this.resultMessage) return;

		this.resultMessage.textContent =
			message;
	}

	handleSkip() {
		this.setError('');
		this.setResultMessage('');

		if (!this.renderResult()) {
			this.setError(
				'Unable to render quiz result.'
			);

			return;
		}

		this.showResult();
	}

	updatePageDescription(activeSlide, isForm) {
		if (!this.pageDescription) return;

		this.pageDescription.classList.toggle(
			'is-invisible',
			isForm
		);

		if (isForm) return;

		if (this.activeIndex === 0) {
			this.pageDescription.innerHTML =
				this.defaultPageDescription;

			return;
		}

		const isMultiple = Boolean(
			activeSlide.querySelector(
				'[data-quiz-option][type="checkbox"]'
			)
		);

		const text = isMultiple
			? Theme.translations.quiz_multiple_answers
			: Theme.translations.quiz_single_answer;

		const paragraph =
			document.createElement('p');

		paragraph.textContent = text;
	}

	disconnectedCallback() {
		window.removeEventListener(
			'resize',
			this.handleResize
		);

		window.clearTimeout(
			this.resizeTimeout
		);
	}

	scrollToNavigation(option) {
		if (
			!option.checked ||
			!this.navigation
		) {
			return;
		}

		window.setTimeout(() => {
			const { bottom } =
				this.navigation.getBoundingClientRect();

			if (bottom <= window.innerHeight) {
				return;
			}

			this.navigation.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'nearest',
			});
		}, 400);
	}
}

if (!customElements.get('quiz-slider')) {
	customElements.define(
		'quiz-slider',
		QuizSlider
	);
}
